<template>
	<div data-component="bot-tricks-table">
		<dynamic-table
			:columns="getColumns"
			:data="getTricks"
			overflow="visible"
		>
			<template #lead="{ row, column, index }">
				<card-select-field
					multiple
					renderOnChange
					hideSelected
					emptyAllowed
					:id="getId(index, column)"
					:rules="getLeadRules(row, column, index)"
					:options="getCardsOptionsWithText(column, index)"
					:value="getCardForSide(row, column)"
					@input="handleCardSelection($event, index, column)"
				/>
			</template>
			<template #lefty="{ row, column, index }">
				<card-select-field
					multiple
					renderOnChange
					hideSelected
					emptyAllowed
					:id="getId(index, column)"
					:rules="getLeftyRules(row, column, index)"
					:options="getCardsOptionsWithText(column, index)"
					:value="getCardForSide(row, column)"
					@input="handleCardSelection($event, index, column)"
				/>
			</template>
			<template #action="{ index }">
				<div data-element="actions-cell">
					<v2-button
						v-if="getCanClear(index)"
						fit
						size="xs"
						@click="handleClear(index)">
						Clear
					</v2-button>
					<v2-button
						v-if="getCanDelete(index)"
						fit
						size="xs"
						variant="red"
						@click="handleDelete(index)">
						Delete
					</v2-button>
				</div>
			</template>
		</dynamic-table>

		<div data-element="actions">
			<v2-button
				fit
				size="xs"
				:disabled="!getIfLastTrickSelected"
				@click="handleAddCondition">
				Add Condition
			</v2-button>
		</div>
	</div>
</template>

<script>

	import CardSelectField from '@/components/dealLibrary/CardSelectField';
	import DynamicTable from '@/components/ui/views/dynamicTable/DynamicTable';
	import V2Button from '@/components/v2/ui/V2Button.vue';
	import actionClick from '@/mixins/actionClick';
	import invalidFormMessage from '@/mixins/invalidFormMessage';
	import { createSuitElement } from '@/helpers';
	import { Game } from '@bridge128/bridge128-game-logic-lib';

	export default {
		components: {
			V2Button,
			CardSelectField,
			DynamicTable
		},
		mixins: [actionClick, invalidFormMessage],
		props: {
			deal: {
				type: Object,
				default: undefined
			},
			condition: {
				type: Array,
				default () {
					return [];
				}
			},
			lead: {
				type: Object,
				default: undefined
			},
			lefty: {
				type: Object,
				default: undefined
			},
			index: {
				type: Number,
				default: undefined
			}
		},
		data: () => ({
			suitsDataMap: {
				H: {
					symbol: '♥',
					colour: 'red'
				},
				D: {
					symbol: '♦',
					colour: 'red'
				},
				S: {
					symbol: '♠',
					colour: 'black'
				},
				C: {
					symbol: '♣',
					colour: 'black'
				}
			}
		}),
		computed: {
			getGameData () {
				if (this.deal) {
					return Game.fromState(this.deal.deal);
				}
				return null;
			},
			getLead () {
				return this.lead;
			},
			getLefty () {
				return this.lefty;
			},
			getColumns () {
				const columns = [
				];

				if (this.getLead && this.getLefty) {
					columns.push(
						{
							...this.getLead,
							label: `If ${this.getLead.name} leads`,
							key: 'lead',
							index: 0
						},
						{
							...this.getLefty,
							label: `${this.getLefty.name} plays`,
							key: 'lefty',
							index: 1
						},
						{
							label: '',
							key: 'action'
						}
					);
				}
				return columns;
			},
			getTricks () {
				if (this.condition?.length) {
					return this.condition;
				}
				return [];
			},
			getIfLastTrickSelected () {
				if (this.getTricks.length) {
					const lastTrick = this.getTricks[this.getTricks.length - 1];
					return !!lastTrick[this.getLead?.short]?.length && !!lastTrick[this.getLefty?.short]?.length;
				}
				return false;
			}
		},
		watch: {},
		created () {},
		methods: {
			getCardsOptions (column, index) {
				if (this.getGameData) {
					const player = this.getGameData[`player${column.short}`];
					const excludeCards = this.getExcludedCards(column, index);
					return player.handAsList
						.map(({ name }) => name)
						.filter(card => !excludeCards.includes(card));
				}
				return [];
			},
			getCardsOptionsWithText (column, index) {
				const options = this.getCardsOptions(column, index);
				return options.map(name => {
					const cardValue = name[0];
					const cardSuit = name[1];
					const suit = this.suitsDataMap[cardSuit];
					const text = `<b>${cardValue}</b> ${createSuitElement(suit.symbol, suit.colour)}`;
					return {
						value: name,
						text
					};
				});
			},
			getCardForSide (row, column) {
				if (row && row[column.short]) {
					return row[column.short];
				}
			},
			getId (index, column) {
				return `card-${this.index}-${index}-${column.short}`;
			},
			getLeadRules (row, column, index) {
				return {
					// required: true,
					sameSuit: true
				};
			},
			getLeftyRules (row, column, index) {
				const leadSide = this.getLead?.short;
				let trickSuit;
				const leadCards = row[leadSide];
				if (leadCards?.length) {
					trickSuit = leadCards[0][1];
				}

				const tricks = this.getTricks;
				const prevTricks = tricks.slice(0, index);
				const conditionsCards = prevTricks.map(trick => trick[column.short]).flat();
				const currentValue = this.getCardForSide(row, column);
				const prevConditionsCards = this.removeValues(conditionsCards, currentValue);
				const hand = this.getCardsOptions(column, index);
				const availableHand = this.removeValues(hand, prevConditionsCards);

				return {
					// required: true,
					canPlayCardsToTrick: {
						trickSuit,
						hand: availableHand
					}
				};
			},
			getExcludedCards (column, index) {
				const cards = this.getTricks
					.map((trick, trickIndex) => index === trickIndex ? false : trick[column.short])
					.filter(Boolean);
				// TODO: Not sure if we need to exclude cards from leads
				// if (column.short === this.getLead.short) {
				// 	return cards.flat();
				// }
				return cards;
			},
			handleCardSelection (card, index, column) {
				this.$emit('onCardSelection', this.index, index, card, column);
			},
			handleAddCondition () {
				if (this.getIfLastTrickSelected) {
					this.$emit('onAddCondition', this.index);
				}
			},
			getCanClear (index) {
				const tricksMaxIndex = this.getTricks.length - 1;
				return tricksMaxIndex === index;
			},
			getCanDelete (index) {
				if (index === 0) {
					return false;
				}
				return this.getCanClear(index);
			},
			removeValues (arr, valuesToRemove) {
				return arr.filter(item => !valuesToRemove.includes(item));
			},
			handleClear (index) {
				this.$emit('onClear', this.index, index);
			},
			handleDelete (index) {
				this.$emit('onDelete', this.index, index);
			}
		}
	};
</script>

<style lang="scss" scoped>
	[data-component="bot-tricks-table"] {
		::v-deep [data-component='dynamic-table'] {
			@media (max-width: 1279px) {
				overflow: auto !important;
				min-height: 225px;
			}

			[data-element='table-column-lead'],
			[data-element='table-column-lefty'],
			[data-element='table-column-action'] {
				vertical-align: top;
			}

			[data-element='table-column-lead'] {
				width: 180px;
				padding: rem(6) 10px rem(6) 0;
				text-align: left;

				@include mq($below: 'sm') {
					width: 220px;
				}

				[data-component='select-field'] {
					margin: 0;
					width: 100%;

					.multiselect {
						&__tags-wrap {
							display: inline-flex;
							flex-wrap: wrap;
						}
					}
				}
			}

			[data-element='table-column-lefty'] {
				width: 180px;
				padding: rem(6) 0 rem(6) 10px;
				text-align: left;

				[data-component='select-field'] {
					margin: 0;
					width: 100%;

					.multiselect {
						&__tags-wrap {
							display: inline-flex;
							flex-wrap: wrap;
						}
					}
				}
			}

			td {
				&[data-element='table-column-action'] {
					padding-top: rem(12);
				}
			}

			[data-element='table-column-action'] {
				width: 120px;

				[data-element='actions-cell'] {
					display: flex;
					justify-content: flex-end;

					.btn:not(:first-child) {
						margin-left: 6px;
					}
				}
			}
		}

		[data-element="actions"] {
			margin: 10px 0;

			[data-component='btn'] {
				width: fit-content;
			}
		}
	}
</style>
